import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { isValidPhoneNumber, getCountries, getCountryCallingCode, Country, parsePhoneNumber } from 'react-phone-number-input';
import { ChevronDown, Search, Check, AlertCircle } from 'lucide-react';
import flags from 'react-phone-number-input/flags';
import { useThemeMode } from '../../theme/ThemeProvider';

interface PhoneInputFieldProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    lockCountry?: boolean;
    defaultCountry?: Country;
    variant?: 'floating' | 'standard';
    className?: string;
}

const getCountryName = (countryCode: Country): string => {
    try {
        const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
        return regionNames.of(countryCode) || countryCode;
    } catch {
        return countryCode;
    }
};

const FlagIcon: React.FC<{ country: Country; size?: number }> = ({ country, size = 22 }) => {
    const FlagComponent = flags[country];
    if (!FlagComponent) return null;
    return (
        <span
            className="inline-flex items-center justify-center rounded overflow-hidden flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.12),0_0_0_1px_rgba(0,0,0,0.08)]"
            style={{ width: size + 6, height: size - 2 }}
        >
            <FlagComponent title={country} />
        </span>
    );
};

const PhoneInputField: React.FC<PhoneInputFieldProps> = ({
    value,
    onChange,
    label,
    error,
    required = false,
    disabled = false,
    readOnly = false,
    lockCountry = false,
    defaultCountry = 'DE',
    variant = 'floating',
    className = '',
}) => {
    const { isDark } = useThemeMode();
    const [selectedCountry, setSelectedCountry] = useState<Country>(defaultCountry);
    // The default often arrives after mount (the profile's country loads async). Follow it
    // while the field is empty and the person hasn't picked a country themselves.
    const pickedCountry = useRef(false);
    useEffect(() => {
        if (!value && !pickedCountry.current && defaultCountry) setSelectedCountry(defaultCountry);
    }, [defaultCountry, value]);
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [localPhoneNumber, setLocalPhoneNumber] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    const countries = useMemo(() => {
        return getCountries()
            .map((country) => ({
                code: country,
                name: getCountryName(country),
                callingCode: `+${getCountryCallingCode(country)}`,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, []);

    const filteredCountries = useMemo(() => {
        if (!searchQuery) return countries;
        const query = searchQuery.toLowerCase();
        return countries.filter(
            (c) =>
                c.name.toLowerCase().includes(query) ||
                c.callingCode.includes(query) ||
                c.code.toLowerCase().includes(query)
        );
    }, [countries, searchQuery]);

    const updateDropdownPosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownH = 380;
        const openUpward = spaceBelow < dropdownH && rect.top > dropdownH;
        setDropdownStyle({
            position: 'fixed',
            left: rect.left,
            width: 320,
            zIndex: 9999,
            ...(openUpward
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 }),
        });
    }, []);

    useEffect(() => {
        if (value) {
            try {
                const parsed = parsePhoneNumber(value);
                if (parsed) {
                    if (parsed.country) setSelectedCountry(parsed.country);
                    if (parsed.nationalNumber) {
                        setLocalPhoneNumber(parsed.nationalNumber);
                        return;
                    }
                }
            } catch { /* ignore */ }
            const callingCode = `+${getCountryCallingCode(selectedCountry)}`;
            if (value.startsWith(callingCode)) {
                setLocalPhoneNumber(value.slice(callingCode.length));
            } else {
                setLocalPhoneNumber(value.replace(/\D/g, ''));
            }
        } else {
            setLocalPhoneNumber('');
        }
    }, [value, selectedCountry]);

    useEffect(() => {
        const handleOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                containerRef.current && !containerRef.current.contains(target) &&
                dropdownRef.current && !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };
        if (isOpen) {
            updateDropdownPosition();
            document.addEventListener('mousedown', handleOutside);
            window.addEventListener('scroll', updateDropdownPosition, true);
            window.addEventListener('resize', updateDropdownPosition);
        }
        return () => {
            document.removeEventListener('mousedown', handleOutside);
            window.removeEventListener('scroll', updateDropdownPosition, true);
            window.removeEventListener('resize', updateDropdownPosition);
        };
    }, [isOpen, updateDropdownPosition]);

    const handleCountrySelect = (country: Country) => {
        pickedCountry.current = true;
        setSelectedCountry(country);
        setIsOpen(false);
        setSearchQuery('');
        const callingCode = `+${getCountryCallingCode(country)}`;
        if (localPhoneNumber) onChange(`${callingCode}${localPhoneNumber}`);
    };

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, '');
        setLocalPhoneNumber(val);
        const callingCode = `+${getCountryCallingCode(selectedCountry)}`;
        onChange(val ? `${callingCode}${val}` : '');
    };

    const callingCode = `+${getCountryCallingCode(selectedCountry)}`;

    const dropdown = isOpen
        ? ReactDOM.createPortal(
            <div
                ref={dropdownRef}
                style={dropdownStyle}
                className="rounded-xl border-2 border-slate-200 dark:border-brand-400/40 bg-white dark:bg-[#243F6A] shadow-[0_12px_40px_-8px_rgba(0,0,0,0.18)] dark:shadow-[0_24px_80px_-12px_rgba(0,0,0,0.90)] overflow-hidden"
            >
                <div className="p-2 border-b border-slate-100 dark:border-white/[0.10] bg-slate-50 dark:bg-[#1E3356] sticky top-0 z-10">
                    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-white dark:bg-[#1C2E4C] rounded-lg border border-slate-200 dark:border-white/[0.18] focus-within:border-brand-500 dark:focus-within:border-brand-400 transition-colors">
                        <Search size={16} className="text-slate-400 dark:text-slate-300 flex-shrink-0" />
                        <input
                            autoFocus
                            type="text"
                            placeholder="Search countries..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="flex-1 min-w-0 text-[13px] bg-transparent outline-none text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                        />
                    </div>
                </div>

                <div className="max-h-[320px] overflow-y-auto py-0.5 divide-y divide-slate-100 dark:divide-white/[0.06]">
                    {filteredCountries.map((country) => {
                        const isSelected = country.code === selectedCountry;
                        return (
                            <button
                                key={country.code}
                                type="button"
                                onClick={() => handleCountrySelect(country.code)}
                                className={[
                                    'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors',
                                    isSelected
                                        ? 'bg-brand-50 dark:bg-brand-500/25'
                                        : 'hover:bg-slate-50 dark:hover:bg-white/[0.07]',
                                ].join(' ')}
                            >
                                <FlagIcon country={country.code} />
                                <span className={`flex-1 text-[13px] ${isSelected ? 'font-semibold' : ''} text-slate-800 dark:text-slate-100`}>
                                    {country.name}
                                </span>
                                <span className="text-[12px] text-slate-500 dark:text-slate-400 font-medium">
                                    {country.callingCode}
                                </span>
                                {isSelected && <Check size={14} className="text-brand-500 dark:text-brand-400 ml-0.5" />}
                            </button>
                        );
                    })}

                    {filteredCountries.length === 0 && (
                        <div className="px-4 py-8 text-center">
                            <p className="text-[13px] text-slate-400">No countries found</p>
                            <p className="text-[11px] text-slate-400 dark:text-slate-300">Try a different search term</p>
                        </div>
                    )}
                </div>
            </div>,
            document.body
        )
        : null;

    return (
        <div className={`w-full flex flex-col gap-1 ${className}`} ref={containerRef}>
            {label && (
                <label className={[
                    'text-[11.5px] font-medium leading-none select-none text-slate-500 dark:text-slate-400',
                    disabled ? 'opacity-50' : '',
                ].join(' ')}>
                    {label}
                    {required && <span className="text-red-400 ml-0.5">*</span>}
                </label>
            )}

            <div className={[
                'relative flex items-center rounded-lg border bg-white dark:bg-surface-dark-300 transition-all duration-150 h-[40px]',
                error
                    ? 'border-red-300 dark:border-red-500/50 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-500/[0.08]'
                    : 'border-slate-200 dark:border-white/[0.12] focus-within:border-brand-400 dark:focus-within:border-brand-400/60 focus-within:ring-2 focus-within:ring-brand-500/[0.08] hover:border-slate-300 dark:hover:border-white/[0.20]',
                disabled ? 'opacity-50 cursor-not-allowed' : '',
            ].join(' ')}>
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={() => {
                        if (!disabled && !readOnly && !lockCountry) {
                            if (!isOpen) updateDropdownPosition();
                            setIsOpen((o) => !o);
                        }
                    }}
                    disabled={disabled || readOnly || lockCountry}
                    className="flex items-center gap-1 pl-2.5 pr-1.5 h-full rounded-l-lg transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.04] flex-shrink-0"
                >
                    <FlagIcon country={selectedCountry} />
                    <span className="text-[13px] font-medium text-slate-700 dark:text-slate-200">
                        {callingCode}
                    </span>
                    {!readOnly && !disabled && !lockCountry && (
                        <ChevronDown
                            size={12}
                            className={`text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        />
                    )}
                </button>

                <div className="w-px h-5 bg-slate-200 dark:bg-white/[0.10] shrink-0" />

                <input
                    type="tel"
                    value={localPhoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="Enter phone number"
                    disabled={disabled}
                    readOnly={readOnly}
                    className="flex-1 min-w-0 h-full bg-transparent outline-none text-[13.5px] font-normal text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 pl-2.5 pr-2"
                />
            </div>

            {/* Portal dropdown — rendered on document.body so it is never clipped */}
            {dropdown}

            {error && (
                <div className="flex items-center gap-1 mt-0.5">
                    <AlertCircle size={11} className="text-red-500 shrink-0" />
                    <p className="text-[11px] text-red-500">{error}</p>
                </div>
            )}
        </div>
    );
};

export { isValidPhoneNumber };
export default PhoneInputField;
